import React, { useState, useMemo, useCallback } from 'react';
import { contextFactory } from '../../../hooks';

export interface BodyDimensions {
  height: number;
  width: number;
}
export interface ImagePreviewValues {
  src: string;
  alt: string;
  rotationDegree: number;
  zoomValue: number;
  bodyDimensions: BodyDimensions;
}

export interface ImagePreviewActions {
  changeZoom: (value: number) => void;
  setRotationDegree: (degree: number) => void;
  reset: () => void;
  onClose: () => void;
  setBodyDimensions: React.Dispatch<React.SetStateAction<BodyDimensions>>;
}

export const MAX_ZOOM = 3;
export const MIN_ZOOM = 1;

export const [ImagePreviewValuesContext, useImagePreviewValues] = contextFactory<ImagePreviewValues>(
  'Image Preview Values not found in tree'
);

export const [ImagePreviewActionsContext, useImagePreviewActions] = contextFactory<ImagePreviewActions>(
  'Image Preview Actions not found in tree'
);

interface ImagePreviewProviderProps {
  src: string;
  alt: string;
  onClose: () => void;
  children: React.ReactNode;
}

export function ImagePreviewProvider({ src, alt, onClose, children }: ImagePreviewProviderProps) {
  const [zoomValue, setZoomValue] = useState(1);
  const [rotationDegree, setRotationDegree] = useState(0);
  const [bodyDimensions, setBodyDimensions] = useState<BodyDimensions>({
    width: 0,
    height: 0,
  });
  const changeZoom = useCallback((value: number) => {
    setZoomValue(value);
  }, []);

  const reset = useCallback(() => {
    setZoomValue(1);
    setRotationDegree(0);
  }, []);

  const values = useMemo(
    () => ({
      src,
      alt,
      zoomValue,
      rotationDegree,
      bodyDimensions,
    }),
    [src, alt, zoomValue, rotationDegree, bodyDimensions]
  );

  const actions = useMemo(
    () => ({
      changeZoom,
      setRotationDegree,
      reset,
      onClose,
      setBodyDimensions,
    }),
    [changeZoom, setRotationDegree, reset, onClose, setBodyDimensions]
  );

  return (
    <ImagePreviewValuesContext.Provider value={values}>
      <ImagePreviewActionsContext.Provider value={actions}>{children}</ImagePreviewActionsContext.Provider>
    </ImagePreviewValuesContext.Provider>
  );
}
